import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { CollectionPropertyType, ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionOneImageTextApi from "hooks/api/private/blocks/useSectionOneImageTextApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionOneImageTextBody,
    SectionOneImageTextDetails,
} from "types/api/blocks/sectionOneImageText";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type ImageTextForm = {
    id: string | undefined;
    img_id: string | undefined;
    img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            title: string;
            subtitle: string;
            button_label: string;
            button_url: string;
        };
    };
};

type SectionImageTextType = {
    buttonDelete: ButtonDeleteInterface;
    setMainImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    formHook: FormHookType<ImageTextForm>;
};

function useSectionImageText(): SectionImageTextType {
    const { saveSectionOneImageText, getOneSectionOneImageText, deleteSectionOneImageText } =
        useSectionOneImageTextApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const textWithoutValidators: CollectionPropertyType = React.useMemo(
        () => ({
            type: "collection",
            of: {
                type: "object",
                properties: {
                    title: {
                        type: "simple",
                        validators: [],
                    },
                    subtitle: {
                        type: "simple",
                        validators: [],
                    },
                    button_label: {
                        type: "simple",
                        validators: [],
                    },
                    button_url: {
                        type: "simple",
                        validators: [],
                    },
                },
            },
        }),
        []
    );

    const textWithValidators: CollectionPropertyType = React.useMemo(
        () => ({
            type: "collection",
            of: {
                type: "object",
                properties: {
                    title: {
                        type: "simple",
                        validators: [],
                    },
                    subtitle: {
                        type: "simple",
                        validators: [],
                    },
                    button_label: {
                        type: "simple",
                        validators: [notEmptyValidator],
                    },
                    button_url: {
                        type: "simple",
                        validators: [notEmptyValidator],
                    },
                },
            },
        }),
        []
    );

    const [schema, setSchema] = React.useState<ObjectPropertyType>(() => ({
        type: "object",
        properties: {
            id: {
                type: "simple",
                validators: [],
            },
            img_id: {
                type: "simple",
                validators: [],
            },
            img: notEmptyMedia,
            text: textWithoutValidators,
        },
    }));

    const initialValues: ImageTextForm = React.useMemo(() => {
        return {
            id: undefined,
            img_id: undefined,
            img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    subtitle: "",
                    button_label: "",
                    button_url: "",
                },
                en: {
                    title: "",
                    subtitle: "",
                    button_label: "",
                    button_url: "",
                },
            },
        };
    }, []);

    const formHook = useForm<ImageTextForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    React.useEffect(() => {
        setSchema((prevSchema) => {
            const newSchema = _.cloneDeep(prevSchema);

            if (
                form.text.en.button_label ||
                form.text.en.button_url ||
                form.text.it.button_label ||
                form.text.it.button_url
            ) {
                newSchema.properties.text = textWithValidators;
            } else {
                newSchema.properties.text = textWithoutValidators;
            }

            return newSchema;
        });
    }, [
        form.text.en.button_label,
        form.text.en.button_url,
        form.text.it.button_label,
        form.text.it.button_url,
        textWithoutValidators,
        textWithValidators,
    ]);

    const { setDesktopMobileImage: setMainImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img",
    });

    const [sectionOneImageTextDetails, setImageTextDetails] = React.useState<
        SectionOneImageTextDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionOneImageText(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setImageTextDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setImageTextDetails, getOneSectionOneImageText, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionOneImageTextDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionOneImageTextDetails.id;
                newForm.img_id = sectionOneImageTextDetails.img_id;
                newForm.img = {
                    id: sectionOneImageTextDetails.img.id,
                    desktop: createAttachmentType({
                        id: sectionOneImageTextDetails.img.desktop.id,
                        label: "Desktop",
                        filename: sectionOneImageTextDetails.img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionOneImageTextDetails.img.mobile.id,
                        label: "Mobile",
                        filename: sectionOneImageTextDetails.img.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.text.en = sectionOneImageTextDetails.text.en ?? {
                    title: "",
                    subtitle: "",
                    button_label: "",
                    button_url: "",
                };
                newForm.text.it = sectionOneImageTextDetails.text.it ?? {
                    title: "",
                    subtitle: "",
                    button_label: "",
                    button_url: "",
                };

                return newForm;
            });
        }
    }, [setForm, sectionOneImageTextDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionOneImageTextBody: SectionOneImageTextBody = {
                    id: blockGroupCompositionId,
                    img: {
                        id: form.img.id,
                        desktop: converterAttachmentToFileWithPayload(form.img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img.mobile),
                    },
                    text: {
                        it: {
                            title: form.text.it.title,
                            subtitle: form.text.it.subtitle,
                            button_label: form.text.it.button_label,
                            button_url: form.text.it.button_url,
                        },
                        en: {
                            title: form.text.en.title,
                            subtitle: form.text.en.subtitle,
                            button_label: form.text.en.button_label,
                            button_url: form.text.en.button_url,
                        },
                    },
                };

                const promises: Promise<unknown>[] = [];

                const [apiCall] = saveSectionOneImageText(sectionOneImageTextBody);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionOneImageText,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionImageTextType => ({
            buttonDelete: deleteSectionOneImageText,
            setMainImage,
            formHook,
        }),
        [deleteSectionOneImageText, setMainImage, formHook]
    );
}

export default useSectionImageText;
